import { type CSSProperties, useEffect, useRef } from 'react';

import type { MarketingDoorId } from '~/marketing/landingContent';
import { useReducedMotion } from '../../presence/useReducedMotion';
import person0 from '../../assets/marketing/people/person-0.webp';
import person1 from '../../assets/marketing/people/person-1.webp';
import person2 from '../../assets/marketing/people/person-2.webp';
import person3 from '../../assets/marketing/people/person-3.webp';
import person4 from '../../assets/marketing/people/person-4.webp';
import person5 from '../../assets/marketing/people/person-5.webp';
import { circleFormingQuestion } from './circleFormingPrompt';
import './circleForming.css';

/**
 * The hero showpiece: a Circle of nearby people forming around one shared prompt. Six person nodes
 * fade in around a ring, the ring + spoke links draw in, and a light travels the circuit — the
 * product concept ("nearby people → pulled together → one shared prompt") as a living constellation.
 * Replaces the old phone mockup. Fully static under reduced motion.
 */

type Tint = 'warm' | 'cool' | 'mix';
const PEOPLE: { tint: Tint; g: string; photo: string }[] = [
  { tint: 'warm', g: 'M', photo: person0 },
  { tint: 'cool', g: 'J', photo: person1 },
  { tint: 'mix', g: 'A', photo: person2 },
  { tint: 'warm', g: 'R', photo: person3 },
  { tint: 'cool', g: 'S', photo: person4 },
  { tint: 'mix', g: 'L', photo: person5 },
];
const N = PEOPLE.length;
const CX = 50;
const CY = 50;
const R = 39;

const PTS = PEOPLE.map((_, i) => {
  const ang = ((-90 + i * (360 / N)) * Math.PI) / 180;
  return { x: CX + R * Math.cos(ang), y: CY + R * Math.sin(ang) };
});
const RING = PTS.map((a, i) => {
  const b = PTS[(i + 1) % N];
  return { x1: a.x, y1: a.y, x2: b.x, y2: b.y, len: Math.hypot(b.x - a.x, b.y - a.y) };
});
const SPOKES = PTS.map((a) => ({ x1: a.x, y1: a.y, len: Math.hypot(CX - a.x, CY - a.y) }));

function lineStyle(len: number, i: number): CSSProperties {
  // stroke-dashoffset is driven by CSS (var(--len) → 0 on .is-in). Setting it inline here would
  // beat the CSS rule (inline > selector) and the links would never draw in.
  return { strokeDasharray: len, ['--len' as string]: len, ['--i' as string]: i } as CSSProperties;
}

type CircleFormingProps = { motionKilled?: boolean; door?: MarketingDoorId };

export function CircleForming({ motionKilled = false, door }: CircleFormingProps) {
  const question = circleFormingQuestion(door);
  const reduced = useReducedMotion();
  // Honour both OS reduced-motion and the site's own landing-motion kill-switch.
  const stilled = motionKilled || reduced;
  const ref = useRef<HTMLDivElement | null>(null);
  const travRef = useRef<SVGCircleElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const armId = requestAnimationFrame(() => el.classList.add('is-in'));
    // guarantee the links reveal even if the rAF is dropped
    const fallback = window.setTimeout(() => el.classList.add('is-in'), 1400);

    let orbitRaf = 0;
    let io: IntersectionObserver | null = null;
    if (!stilled) {
      const trav = travRef.current;
      let t0: number | null = null;
      let shown = false;
      let offAt: number | null = null;
      const orbit = (ts: number) => {
        if (t0 === null) t0 = ts;
        const elapsed = (ts - t0) / 1000;
        if (trav && elapsed > 2.4) {
          if (!shown) {
            trav.setAttribute('opacity', '0.95');
            shown = true;
          }
          const prog = ((elapsed - 2.4) / 9) % 1; // one lap per 9s
          const seg = prog * N;
          const i = Math.floor(seg);
          const f = seg - i;
          const a = PTS[i % N];
          const b = PTS[(i + 1) % N];
          trav.setAttribute('cx', String(a.x + (b.x - a.x) * f));
          trav.setAttribute('cy', String(a.y + (b.y - a.y) * f));
        }
        orbitRaf = requestAnimationFrame(orbit);
      };
      /**
       * Stop the loop outright when the hero leaves the viewport rather than running it and
       * skipping the writes — an idle rAF callback still costs a frame slot on every scroll tick.
       * `is-off` parks the looping CSS animations at the same time. On return, the clock is shifted
       * forward by the time spent away so the traveller resumes where it stopped instead of jumping.
       */
      io = new IntersectionObserver(
        (entries) => {
          const onScreen = entries[0]?.isIntersecting ?? true;
          el.classList.toggle('is-off', !onScreen);
          if (onScreen && !orbitRaf) {
            if (offAt !== null && t0 !== null) {
              t0 += performance.now() - offAt;
              offAt = null;
            }
            orbitRaf = requestAnimationFrame(orbit);
          } else if (!onScreen && orbitRaf) {
            cancelAnimationFrame(orbitRaf);
            orbitRaf = 0;
            offAt = performance.now();
          }
        },
        { threshold: 0 }
      );
      io.observe(el);
      orbitRaf = requestAnimationFrame(orbit);
    }

    return () => {
      cancelAnimationFrame(armId);
      window.clearTimeout(fallback);
      if (orbitRaf) cancelAnimationFrame(orbitRaf);
      if (io) io.disconnect();
    };
  }, [stilled]);

  return (
    <div
      ref={ref}
      className={`kw-stage${stilled ? ' is-in is-reduced' : ''}`}
      role="img"
      aria-label={`A Circle forming — tonight's prompt: ${question} — 6 gathering nearby`}>
      <div className="kw-stage__ring">
        <div className="kw-stage__haze" aria-hidden />

        <svg
          className="kw-stage__links"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden>
          <defs>
            <linearGradient
              id="kwLinkGrad"
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2="100"
              y2="100">
              <stop offset="0" stopColor="#f4c56d" stopOpacity="0.28" />
              <stop offset="0.5" stopColor="#ffe3a6" stopOpacity="0.72" />
              <stop offset="1" stopColor="#f4c56d" stopOpacity="0.28" />
            </linearGradient>
          </defs>
          <g
            className="kw-ring"
            fill="none"
            stroke="url(#kwLinkGrad)"
            strokeWidth="0.6"
            strokeLinecap="round">
            {RING.map((l, i) => (
              <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} style={lineStyle(l.len, i)} />
            ))}
          </g>
          <g
            className="kw-spokes"
            fill="none"
            stroke="rgba(244,190,110,0.6)"
            strokeWidth="0.42"
            strokeLinecap="round">
            {SPOKES.map((l, i) => (
              <line key={i} x1={l.x1} y1={l.y1} x2={CX} y2={CY} style={lineStyle(l.len, i)} />
            ))}
          </g>
          <circle ref={travRef} className="kw-trav" r="0.9" fill="#ffe3a6" opacity="0" />
        </svg>

        <div className="kw-people">
          {PEOPLE.map((p, i) => (
            <div
              key={p.g + i}
              className={`kw-person kw-person--${p.tint}`}
              style={
                {
                  left: `${PTS[i].x}%`,
                  top: `${PTS[i].y}%`,
                  ['--d' as string]: `${0.15 + i * 0.15}s`,
                } as CSSProperties
              }>
              <div className="kw-person__halo" aria-hidden />
              <div className="kw-person__ring" aria-hidden />
              <div className="kw-person__disc" aria-hidden>
                <img className="kw-person__photo" src={p.photo} alt="" loading="lazy" />
              </div>
            </div>
          ))}
        </div>

        <div className="kw-prompt">
          <div className="kw-prompt__label">
            <span className="kw-prompt__ember" aria-hidden />
            Tonight's prompt
          </div>
          <div className="kw-prompt__q">{question}</div>
          <div className="kw-prompt__meta">
            <span className="kw-prompt__live" aria-hidden />6 gathering nearby
          </div>
        </div>
      </div>

      <div className="kw-stage__caption" aria-hidden>
        <b>Nearby people</b> → pulled together → <b>one shared prompt</b>
      </div>
    </div>
  );
}
